/** Mobile emergency stop floating action button.
 *  Red FAB visible on mobile, long-press (2s) activates confirmation sheet.
 */

import { useState, useRef, useCallback } from 'react'

interface EmergencyStopFabProps {
  /** Called when emergency stop is confirmed. */
  onConfirm?: () => void
}

export function EmergencyStopFab({ onConfirm }: EmergencyStopFabProps) {
  const [pressing, setPressing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [showConfirm, setShowConfirm] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const holdDuration = 2000 // 2 seconds
  const tickInterval = 50 // update every 50ms

  const startPress = useCallback(() => {
    setPressing(true)
    setProgress(0)

    let elapsed = 0
    intervalRef.current = setInterval(() => {
      elapsed += tickInterval
      const pct = Math.min(elapsed / holdDuration, 1)
      setProgress(pct)

      if (pct >= 1) {
        if (intervalRef.current) clearInterval(intervalRef.current)
        intervalRef.current = null
        setPressing(false)
        setProgress(0)
        setShowConfirm(true)
        // Haptic feedback if available
        if (navigator.vibrate) navigator.vibrate(200)
      }
    }, tickInterval)
  }, [holdDuration])

  const endPress = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setPressing(false)
    setProgress(0)
  }, [])

  const handleConfirm = useCallback(() => {
    setShowConfirm(false)
    onConfirm?.()
  }, [onConfirm])

  const handleCancel = useCallback(() => {
    setShowConfirm(false)
  }, [])

  return (
    <>
      {/* FAB — visible only on mobile (below sm) */}
      <button
        onMouseDown={startPress}
        onMouseUp={endPress}
        onMouseLeave={endPress}
        onTouchStart={startPress}
        onTouchEnd={endPress}
        onTouchCancel={endPress}
        className="fixed bottom-18 right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-red-600 shadow-lg shadow-red-900/50 active:bg-red-700 sm:hidden"
        aria-label="Emergency stop (hold for 2 seconds)"
      >
        {/* Progress ring */}
        {pressing && (
          <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 56 56">
            <circle
              cx="28"
              cy="28"
              r="25"
              fill="none"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="3"
            />
            <circle
              cx="28"
              cy="28"
              r="25"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeDasharray={`${progress * 157} 157`}
              strokeLinecap="round"
            />
          </svg>
        )}
        {/* Stop icon */}
        <svg
          className="h-6 w-6 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
          />
        </svg>
      </button>

      {/* Confirmation sheet */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-red-950/95 sm:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Emergency stop confirmation"
        >
          <div className="text-center px-8">
            <svg
              className="mx-auto h-16 w-16 text-red-400 mb-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
            <h2 className="text-2xl font-bold text-white mb-2">HALT ALL TRADING</h2>
            <p className="text-sm text-red-300 mb-8">
              This will prevent new trades. Check broker open orders immediately.
            </p>
            <div className="flex flex-col gap-3 w-full max-w-xs mx-auto">
              <button
                onClick={handleConfirm}
                className="rounded-lg bg-red-600 px-6 py-3 text-lg font-bold text-white hover:bg-red-500 transition-colors"
              >
                Confirm Emergency Stop
              </button>
              <button
                onClick={handleCancel}
                className="rounded-lg border border-red-700 px-6 py-3 text-sm text-red-300 hover:bg-red-900/50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
